/**
 * Página de perfil de usuario — identidad consolidada vía AuthContext.
 */
import React, { useRef, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import useWorkspaceEmpresa from '../../hooks/useWorkspaceEmpresa';
import userProfileService from '../../services/userProfile.service';
import { getUserFullName, getUserRoleLabel } from '../../utils/userIdentity';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import PasswordInput from '../../components/common/PasswordInput';
import UserAvatar from '../../components/common/UserAvatar';
import {
  FaUser, FaSave, FaKey, FaUserCircle,
  FaEnvelope, FaIdCard, FaCalendarAlt, FaStore, FaBuilding, FaCamera
} from 'react-icons/fa';

const Perfil = () => {
  const {
    currentUser,
    updateUserProfile,
    reloadUserProfile,
    orgId,
    sucursalSeleccionada
  } = useAuth();
  const { nombreEmpresa } = useWorkspaceEmpresa();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: ''
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);

  const [passwordData, setPasswordData] = useState({
    password_actual: '',
    password_nueva: '',
    password_confirmacion: ''
  });

  const [loading, setLoading] = useState(false);
  const [passwordMode, setPasswordMode] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setFormData({
        nombre: currentUser.nombre || '',
        apellido: currentUser.apellido || ''
      });
      setAvatarPreview(currentUser.avatarUrl || currentUser.photoURL || null);
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Seleccioná una imagen válida');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen debe pesar menos de 2 MB');
      return;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser?.id) return;

    try {
      setLoading(true);
      let avatarUrl = currentUser.avatarUrl || currentUser.photoURL || null;

      if (avatarFile && orgId) {
        avatarUrl = await userProfileService.uploadAvatar(orgId, currentUser.id, avatarFile);
      }

      const updated = await userProfileService.updateMyProfile({
        nombre: formData.nombre.trim(),
        apellido: formData.apellido.trim(),
        ...(avatarUrl ? { avatarUrl } : {})
      });

      updateUserProfile(updated);
      await reloadUserProfile();
      setAvatarFile(null);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('❌ Error al actualizar perfil:', error);
      toast.error(error.message || 'Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.password_nueva !== passwordData.password_confirmacion) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordData.password_nueva.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    toast.info('Cambio de contraseña desde perfil: pendiente de endpoint seguro para autogestión.');
  };

  const ultimoAcceso = currentUser?.ultimo_acceso
    ? new Date(currentUser.ultimo_acceso).toLocaleString('es-AR')
    : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-e0e3-foreground">Mi Perfil</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Información del Usuario" icon={<FaUserCircle />}>
          <div className="space-y-4">
            <div className="flex flex-col items-center mb-4 gap-3">
              <UserAvatar user={{ ...currentUser, avatarUrl: avatarPreview }} size="xl" />
              <p className="text-center text-lg font-semibold text-e0e3-foreground">
                {getUserFullName(currentUser)}
              </p>
              <p className="text-sm text-e0e3-muted">{getUserRoleLabel(currentUser)}</p>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-e0e3-muted">
                <FaEnvelope /> {currentUser?.email}
              </div>
              <div className="flex items-center gap-2 text-e0e3-muted">
                <FaIdCard /> Rol: {getUserRoleLabel(currentUser) || '—'}
              </div>
              {nombreEmpresa && (
                <div className="flex items-center gap-2 text-e0e3-muted">
                  <FaBuilding /> {nombreEmpresa}
                </div>
              )}
              {sucursalSeleccionada?.nombre && (
                <div className="flex items-center gap-2 text-e0e3-muted">
                  <FaStore /> {sucursalSeleccionada.nombre}
                </div>
              )}
              {currentUser?.fecha_creacion && (
                <div className="flex items-center gap-2 text-e0e3-muted">
                  <FaCalendarAlt />
                  Miembro desde: {new Date(currentUser.fecha_creacion).toLocaleDateString('es-AR')}
                </div>
              )}
              {ultimoAcceso && (
                <div className="flex items-center gap-2 text-e0e3-muted">
                  <FaCalendarAlt /> Último acceso: {ultimoAcceso}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-white/10">
              <Button
                color="secondary"
                onClick={() => setPasswordMode(!passwordMode)}
                icon={<FaKey />}
                fullWidth
              >
                {passwordMode ? 'Cancelar' : 'Cambiar Contraseña'}
              </Button>
            </div>
          </div>
        </Card>

        <div className="lg:col-span-2">
          {!passwordMode ? (
            <Card title="Editar Perfil" icon={<FaUser />}>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-e0e3-muted mb-2">Foto de perfil</label>
                    <div className="flex items-center gap-3">
                      <UserAvatar user={{ ...currentUser, avatarUrl: avatarPreview }} size="lg" />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={handleAvatarPick}
                      />
                      <Button
                        type="button"
                        color="secondary"
                        icon={<FaCamera />}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Cambiar foto
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-e0e3-muted mb-1">Nombre</label>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        className="nexo-field"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-e0e3-muted mb-1">Apellido</label>
                      <input
                        type="text"
                        name="apellido"
                        value={formData.apellido}
                        onChange={handleChange}
                        className="nexo-field"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-e0e3-muted mb-1">Correo electrónico</label>
                    <input
                      type="email"
                      value={currentUser?.email || ''}
                      className="nexo-field opacity-70"
                      readOnly
                      title="El correo se gestiona desde la cuenta de acceso"
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button type="submit" color="primary" loading={loading} icon={<FaSave />}>
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              </form>
            </Card>
          ) : (
            <Card title="Cambiar Contraseña" icon={<FaKey />}>
              <form onSubmit={handlePasswordSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-e0e3-muted mb-1">Contraseña Actual</label>
                    <PasswordInput
                      name="password_actual"
                      value={passwordData.password_actual}
                      onChange={handlePasswordChange}
                      className="nexo-field"
                      autoComplete="current-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-e0e3-muted mb-1">Nueva Contraseña</label>
                    <PasswordInput
                      name="password_nueva"
                      value={passwordData.password_nueva}
                      onChange={handlePasswordChange}
                      className="nexo-field"
                      minLength={6}
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-e0e3-muted mb-1">Confirmar Contraseña</label>
                    <PasswordInput
                      name="password_confirmacion"
                      value={passwordData.password_confirmacion}
                      onChange={handlePasswordChange}
                      className="nexo-field"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="flex justify-between pt-4">
                    <Button type="button" color="secondary" onClick={() => setPasswordMode(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" color="primary" icon={<FaSave />}>
                      Actualizar Contraseña
                    </Button>
                  </div>
                </div>
              </form>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Perfil;
